import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";
import { UUID } from "crypto";
import { useMemo } from "react";
import api from "@/app/api";
import { partnerKeys } from "../keys/partners";
import { clientGroupKeys } from "../keys/clientGroups";
import { pricingKeys } from "../keys/pricing";
import {
  PartnerType,
  type Partner,
  type PartnerAddress,
  type PartnerContact,
  type PartnerCreate,
  type PartnerUpdate,
  type PartnerContactRequest,
  type PartnerContactUpdate,
  type PartnerAddressCreate,
  type PartnerAddressUpdate,
  type PartnerResponse,
} from "@/app/api/types/partner";
import type { Partner as V2Partner } from "@stockline/shared/types/crm/partner";
import type { PartnerAddress as V2PartnerAddress } from "@stockline/shared/types/crm/partner-address";
import type { PartnerContact as V2PartnerContact } from "@stockline/shared/types/crm/partner-contact";
import {
  usePartners as usePartnersV2,
  usePartnerAddressMutations,
  usePartnerContactMutations,
} from "@stockline/web";
import type { PartnerFilters } from "../types";
import { useGlobalPartners, useGlobalPartnerAccounts } from "./useUnifiedCache";
import { filterAndSearchPartners } from "@/lib/search";

const PARTNER_TYPE_FROM_V2: Record<V2Partner["type"], PartnerType> = {
  CLIENT: PartnerType.CLIENT,
  SUPPLIER: PartnerType.SUPPLIER,
  WHOLESALER: PartnerType.WHOLESALER,
};

// Sentinel UUID used when a hook receives a null partnerId — keeps the V2
// reactive query running unconditionally without ever matching a real row.
// Exported as the single source of truth for every call site that has to
// guard a Rules-of-Hooks-bound reactive partner query.
export const NO_MATCH_PARTNER_ID = "00000000-0000-0000-0000-000000000000";

function v2PartnerToLegacy(v2: V2Partner): Partner {
  return {
    id: v2.id as UUID,
    company_id: v2.companyId as UUID,
    name: v2.name,
    usage_name: v2.usageName,
    code: v2.code,
    type: PARTNER_TYPE_FROM_V2[v2.type],
    ai_context: v2.aiContext,
    is_active: v2.isActive,
    client_groups: [],
  };
}

/**
 * Adapter: V2 (camelCase, Dexie/sync shape) → legacy REST (snake_case) shape.
 * Exported so feature call sites can consume the offline-first reactive
 * hooks from `@stockline/web` while continuing to feed downstream components
 * that still speak the legacy REST shape (e.g. ShippingAddressSelector,
 * PartnerInfo address cards, partners/page edit-dialog state seeding).
 */
export function v2PartnerAddressToLegacy(v2: V2PartnerAddress): PartnerAddress {
  return {
    id: v2.id as UUID,
    partner_id: v2.partnerId as UUID,
    name: v2.name,
    address_1: v2.address1,
    address_2: v2.address2,
    city: v2.city,
    zip_code: v2.zipCode,
    code: v2.code,
    state: v2.state,
    country: v2.country,
    is_default: v2.isDefault,
    is_billing: v2.isBilling ?? false,
    ai_context: v2.aiContext,
  };
}

export function v2PartnerContactToLegacy(v2: V2PartnerContact): PartnerContact {
  return {
    id: v2.id as UUID,
    partner_id: v2.partnerId as UUID,
    company_owner: v2.companyOwner,
    name: v2.name,
    surname: v2.surname,
    phones: v2.phones,
    emails: v2.emails,
    role: v2.role,
    ai_context: v2.aiContext,
  };
}

// Strip the snake_case `Symbol`/`UndefinedType` sentinels that legacy REST
// callers used to encode "leave field unchanged" — the V2 mutation path
// uses real `undefined`, so unset becomes "not present" and present-but-
// `null` becomes "clear field". Callers like `partners/page.tsx` already
// guard with `typeof !== "symbol"` for symbol values, but defensive here.
function stripSentinel<T>(value: T): T | undefined {
  return typeof value === "symbol" ? undefined : value;
}

/**
 * THE MASTER PARTNER QUERY - Now uses unified global cache with client-side filtering
 * All filtering and search happens client-side using the search engine
 */
export function usePartners(filters: PartnerFilters = {}) {
  // Load global partners cache (loads all partners once, then polls every 5min)
  // Support enabled flag for lazy loading (e.g., only load when dropdown opens)
  const globalQuery = useGlobalPartners({ enabled: filters.enabled });

  // Apply client-side filtering and search
  const filteredData = useMemo(() => {
    if (!globalQuery.data) return [];

    // Map filter type to number for filtering
    let typeFilter: number | null = null;
    if (filters.type === "client") {
      typeFilter = 1; // CLIENT
    } else if (filters.type === "supplier") {
      typeFilter = 2; // SUPPLIER
    } else if (filters.type === "wholesaler") {
      typeFilter = 3; // WHOLESALER
    }

    const base = filters.excludeInactive
      ? globalQuery.data.filter((p) => p.is_active !== false)
      : globalQuery.data;

    return filterAndSearchPartners(base, {
      search: filters.search,
      type: typeFilter,
    });
  }, [globalQuery.data, filters.search, filters.type, filters.excludeInactive]);

  // For backward compatibility, return the same interface as before
  return {
    data: filteredData,
    total: filteredData.length,
    isLoading: globalQuery.isLoading,
    isError: globalQuery.isError,
    error: globalQuery.error,
    isFetching: globalQuery.isFetching,
    refetch: globalQuery.refetch,
    // Deprecated infinite scroll props (no-ops for backward compatibility)
    fetchNextPage: async () => {},
    hasNextPage: false,
    isFetchingNextPage: false,
  };
}

export function usePartner(id: UUID | null) {
  const queryClient = useQueryClient();
  // V2 reactive Dexie — surfaces a partial partner instantly while the REST
  // fetch is in flight (offline-first paint).
  const v2Partners = usePartnersV2();

  const v2Match = useMemo(() => {
    if (!id) return undefined;
    return (v2Partners ?? []).find((p) => p.id === id);
  }, [v2Partners, id]);

  return useQuery({
    queryKey: partnerKeys.detail(id!),
    queryFn: async () => {
      // Prefer the global REST cache — it carries the full Partner shape
      // (`client_groups`, `addresses`, `contacts`, etc.) that V2 doesn't.
      const globalCache = queryClient.getQueryData<Partner[]>([
        "partners",
        "global",
      ]);
      const cached = globalCache?.find((p) => p.id === id);
      if (cached) return cached;
      // Always fall through to REST for the full record. Returning a V2
      // mapping here would silently drop client_groups / addresses, so we
      // only use V2 as `placeholderData` below.
      return api.partners.getPartnerId(id!);
    },
    // Render V2's basic fields immediately while queryFn fetches the full
    // shape; once REST returns, TanStack Query swaps to the complete record.
    placeholderData: v2Match ? v2PartnerToLegacy(v2Match) : undefined,
    enabled: !!id,
  });
}

// Partner mutations
export function useCreatePartner() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PartnerCreate) => api.partners.post(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: partnerKeys.all });
      // Invalidate client groups since partner was added to groups
      queryClient.invalidateQueries({ queryKey: clientGroupKeys.all });
    },
  });
}

export function useUpdatePartner() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: UUID; data: PartnerUpdate }) =>
      api.partners.putPartnerId(id, data),
    onMutate: async ({ id, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: partnerKeys.detail(id) });
      await queryClient.cancelQueries({ queryKey: ["partners", "global"] });

      // Snapshot previous value
      const previousPartner = queryClient.getQueryData<PartnerResponse>(
        partnerKeys.detail(id),
      );
      const previousGlobalCache = queryClient.getQueryData<Partner[]>([
        "partners",
        "global",
      ]);

      // Helper to filter out Undefined values
      const getUpdates = (updateData: any): Partial<PartnerResponse> => {
        const updates: Partial<PartnerResponse> = {};
        Object.keys(updateData).forEach((key) => {
          const value = updateData[key];
          if (value !== undefined && typeof value !== "symbol") {
            (updates as any)[key] = value;
          }
        });
        return updates;
      };

      const updates = getUpdates(data);

      // If updating client_group_ids, also update client_groups relationship
      if (
        data.client_group_ids !== undefined &&
        typeof data.client_group_ids !== "symbol" &&
        Array.isArray(data.client_group_ids)
      ) {
        const allGroups = queryClient.getQueryData<any[]>(
          clientGroupKeys.list(),
        );
        if (allGroups) {
          const groupIds = data.client_group_ids;
          (updates as any).client_groups = allGroups.filter((g) =>
            groupIds.includes(g.id),
          );
        }
      }

      // Optimistically update detail cache
      if (previousPartner) {
        queryClient.setQueryData<PartnerResponse>(
          partnerKeys.detail(id),
          (old) => (old ? { ...old, ...updates } : old),
        );
      }

      // Optimistically update global cache
      queryClient.setQueryData<Partner[]>(["partners", "global"], (old) => {
        if (!old) return old;
        return old.map((p) => (p.id === id ? { ...p, ...updates } : p));
      });

      return { previousPartner, previousGlobalCache };
    },
    onError: (_, { id }, context) => {
      // Rollback on error
      if (context?.previousPartner) {
        queryClient.setQueryData(
          partnerKeys.detail(id),
          context.previousPartner,
        );
      }
      if (context?.previousGlobalCache) {
        queryClient.setQueryData(
          ["partners", "global"],
          context.previousGlobalCache,
        );
      }
    },
    onSuccess: (updatedPartner) => {
      // Update caches with server response
      queryClient.setQueryData(
        partnerKeys.detail(updatedPartner.id),
        updatedPartner,
      );
    },
    onSettled: (updatedPartner) => {
      // Refetch to ensure sync
      if (updatedPartner) {
        queryClient.invalidateQueries({
          queryKey: partnerKeys.detail(updatedPartner.id),
        });
        // Invalidate partner groups since they may have changed
        queryClient.invalidateQueries({
          queryKey: clientGroupKeys.partnerGroups(updatedPartner.id),
        });
        // Invalidate effective partner prices since group membership affects pricing
        queryClient.invalidateQueries({
          queryKey: pricingKeys.effectivePartnerPrices(updatedPartner.id),
        });
      }
      queryClient.invalidateQueries({ queryKey: partnerKeys.all });
      // Invalidate client groups since partner-group relationships may have changed
      queryClient.invalidateQueries({ queryKey: clientGroupKeys.all });
    },
  });
}

export function useDeletePartner() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: UUID) => api.partners.deletePartnerId(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: partnerKeys.all });
    },
  });
}

// Partner Contact mutations
//
// All six contact/address mutation hooks below preserve the legacy TanStack
// shape (`mutateAsync`, `isPending`, `isError`, etc.) so existing call sites
// don't change. Internally they route through the V2 mutation hooks from
// `@stockline/web`, which handle the offline-first path: Dexie write →
// change-queue enqueue → V2 push-batch (`POST /api/v2/partner-{addresses,
// contacts}/changes`). The legacy snake_case payloads coming from
// `partners/page.tsx` are mapped to camelCase at each boundary, and the
// returned values are converted back via `v2Partner{Address,Contact}ToLegacy`
// so any consumer that depends on `mutateAsync`'s resolved value gets the
// legacy shape it expects.
export function useCreatePartnerContact() {
  const { createPartnerContact } = usePartnerContactMutations();

  return useMutation({
    mutationFn: async ({
      partnerId,
      data,
    }: {
      partnerId: UUID;
      data: PartnerContactRequest;
    }): Promise<PartnerContact> => {
      const v2 = await createPartnerContact({
        partnerId: partnerId as string,
        name: data.name,
        surname: data.surname,
        companyOwner: stripSentinel(data.company_owner) ?? false,
        phones: stripSentinel(data.phones) ?? null,
        emails: stripSentinel(data.emails) ?? null,
        role: stripSentinel(data.role) ?? null,
        aiContext: stripSentinel(data.ai_context) ?? null,
      });
      return v2PartnerContactToLegacy(v2);
    },
  });
}

export function useUpdatePartnerContact() {
  const { updatePartnerContact } = usePartnerContactMutations();

  return useMutation({
    mutationFn: async ({
      contactId,
      data,
    }: {
      contactId: UUID;
      data: PartnerContactUpdate;
    }): Promise<{ id: UUID }> => {
      await updatePartnerContact(contactId as string, {
        name: stripSentinel(data.name) as string | undefined,
        surname: stripSentinel(data.surname) as string | undefined,
        companyOwner: stripSentinel(data.company_owner) as boolean | undefined,
        phones: stripSentinel(data.phones) as string[] | null | undefined,
        emails: stripSentinel(data.emails) as string[] | null | undefined,
        role: stripSentinel(data.role) as string | null | undefined,
        aiContext: stripSentinel(data.ai_context) as string | null | undefined,
      });
      return { id: contactId };
    },
  });
}

export function useDeletePartnerContact() {
  const { deletePartnerContact } = usePartnerContactMutations();

  return useMutation({
    mutationFn: async (contactId: UUID): Promise<{ id: UUID }> => {
      await deletePartnerContact(contactId as string);
      return { id: contactId };
    },
  });
}

// Partner Address mutations
export function useCreatePartnerAddress() {
  const { createPartnerAddress } = usePartnerAddressMutations();

  return useMutation({
    mutationFn: async ({
      partnerId,
      data,
    }: {
      partnerId: UUID;
      data: PartnerAddressCreate;
    }): Promise<PartnerAddress> => {
      const v2 = await createPartnerAddress({
        partnerId: partnerId as string,
        name: data.name,
        address1: stripSentinel(data.address_1) ?? null,
        address2: stripSentinel(data.address_2) ?? null,
        city: stripSentinel(data.city) ?? null,
        zipCode: stripSentinel(data.zip_code) ?? null,
        code: stripSentinel(data.code) ?? null,
        state: stripSentinel(data.state) ?? null,
        country: stripSentinel(data.country) ?? null,
        isDefault: stripSentinel(data.is_default) ?? false,
        aiContext: stripSentinel(data.ai_context) ?? null,
      });
      return v2PartnerAddressToLegacy(v2);
    },
  });
}

export function useUpdatePartnerAddress() {
  const { updatePartnerAddress } = usePartnerAddressMutations();

  return useMutation({
    mutationFn: async ({
      addressId,
      data,
    }: {
      // `partnerId` is no longer needed by the V2 update path (the row is
      // looked up by id), but we keep it in the signature so existing call
      // sites compile unchanged.
      partnerId: UUID;
      addressId: UUID;
      data: PartnerAddressUpdate;
    }): Promise<{ id: UUID }> => {
      await updatePartnerAddress(addressId as string, {
        name: stripSentinel(data.name) as string | undefined,
        address1: stripSentinel(data.address_1) as string | null | undefined,
        address2: stripSentinel(data.address_2) as string | null | undefined,
        city: stripSentinel(data.city) as string | null | undefined,
        zipCode: stripSentinel(data.zip_code) as string | null | undefined,
        code: stripSentinel(data.code) as string | null | undefined,
        state: stripSentinel(data.state) as string | null | undefined,
        country: stripSentinel(data.country) as string | null | undefined,
        isDefault: stripSentinel(data.is_default) as boolean | undefined,
        aiContext: stripSentinel(data.ai_context) as string | null | undefined,
      });
      return { id: addressId };
    },
  });
}

export function useDeletePartnerAddress() {
  const { deletePartnerAddress } = usePartnerAddressMutations();

  return useMutation({
    mutationFn: async ({
      addressId,
    }: {
      partnerId: UUID;
      addressId: UUID;
    }): Promise<{ id: UUID }> => {
      await deletePartnerAddress(addressId as string);
      return { id: addressId };
    },
  });
}

// CRM Analytics hooks

/**
 * Get aggregated statistics for a partner's order history
 * Returns comprehensive analytics including totals, averages, and date ranges
 */
export function usePartnerStatistics(partnerId: UUID | null) {
  return useQuery({
    queryKey: partnerKeys.statistics(partnerId!),
    queryFn: () => api.partners.getPartnerIdStatistics(partnerId!),
    enabled: !!partnerId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Get paginated product statistics for a partner with sorting
 * Returns per-product analytics with pagination support
 */
export function usePartnerProductStatistics(
  partnerId: UUID | null,
  params: {
    page?: number;
    size?: number;
    sortBy?: string;
    sortOrder?: string;
  } = {},
) {
  return useQuery({
    queryKey: [
      ...partnerKeys.productStats(partnerId!),
      params.page || 1,
      params.size || 50,
      params.sortBy || "total_value",
      params.sortOrder || "desc",
    ],
    queryFn: () =>
      api.partners.getPartnerIdProducts(partnerId!, {
        page: params.page,
        size: params.size,
        sort_by: params.sortBy,
        sort_order: params.sortOrder,
      }),
    enabled: !!partnerId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Get partner pre-orders with date filtering for CRM
 * Returns ALL pre-orders matching the filters (no pagination limit)
 * Includes embedded flows and product information
 */
export function usePartnerPreOrdersFiltered(
  partnerId: UUID | null,
  filters: {
    startDate?: string | null; // YYYY-MM-DD
    endDate?: string | null; // YYYY-MM-DD
    dateField?: "delivery_date" | "order_date" | "created_at";
  } = {},
) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [
      ...partnerKeys.all,
      "pre-orders-filtered",
      partnerId,
      filters.startDate,
      filters.endDate,
      filters.dateField || "delivery_date",
    ],
    queryFn: async () => {
      const preOrders = await api.partners.getPartnerIdPreOrders(partnerId!, {
        start_date: filters.startDate || undefined,
        end_date: filters.endDate || undefined,
        date_field: filters.dateField || "delivery_date",
      });

      // Cache individual pre-orders
      preOrders.forEach((preOrder: any) => {
        queryClient.setQueryData(
          [...partnerKeys.all, "pre-order", preOrder.id],
          preOrder,
        );
      });

      return preOrders;
    },
    enabled: !!partnerId,
    staleTime: 30 * 1000, // 30 seconds - shorter because filters change frequently
  });
}

// Partner Order History (lean recap pattern with infinite scroll)

export function usePartnerOrderHistory(partnerId: UUID | null) {
  const queryClient = useQueryClient();
  const pageSize = 10;

  const query = useInfiniteQuery<
    { items: any[]; total: number; page: number; page_size: number },
    Error
  >({
    queryKey: partnerKeys.orderHistory(partnerId!),
    queryFn: async ({ pageParam }) => {
      const result = await api.partners.getPartnerOrderHistory(partnerId!, {
        page: pageParam as number,
        page_size: pageSize,
      });
      // Enrich lean dicts from global caches (products, units, partners)
      const { enrichRecapPreOrders } = await import(
        "./usePreOrders/cacheHelpers"
      );
      enrichRecapPreOrders(queryClient, result.items);
      return result;
    },
    getNextPageParam: (lastPage) => {
      const totalPages = Math.ceil(lastPage.total / lastPage.page_size);
      return lastPage.page < totalPages ? lastPage.page + 1 : undefined;
    },
    initialPageParam: 1,
    enabled: !!partnerId,
    staleTime: 0, // Always refetch, show cached data while loading
  });

  const allItems = query.data?.pages.flatMap((p) => p.items) ?? [];
  const total = query.data?.pages[0]?.total ?? 0;

  return {
    ...query,
    items: allItems,
    total,
  };
}

// Partner Account (Financial) hooks

/**
 * Get partner account (financial) information
 * Returns comprehensive accounting data including debt, credit limit, and status
 *
 * OPTIMIZED: Uses global cache that's populated by a bulk endpoint
 * The global cache is loaded once and refreshed every 5 minutes
 */
export function usePartnerAccount(partnerId: UUID | null) {
  // Skip if the partnerId is not a valid UUID (e.g., "no-partner-" synthetic IDs or "temp-partner-" offline IDs)
  const partnerIdStr = partnerId?.toString() || "";
  const isValidUUID =
    partnerId &&
    !partnerIdStr.startsWith("no-partner-") &&
    !partnerIdStr.startsWith("temp-partner-");

  // Ensure the global cache is being populated
  // This will trigger the bulk load if not already loaded
  const { data: globalAccountsCache } = useGlobalPartnerAccounts({
    enabled: !!isValidUUID,
  });

  return useQuery({
    queryKey: partnerKeys.account(partnerId!),
    queryFn: async () => {
      // Return null for invalid partner IDs
      if (!isValidUUID) {
        return null;
      }

      // First check the global cache
      if (globalAccountsCache && globalAccountsCache.has(partnerId!)) {
        const cachedAccount = globalAccountsCache.get(partnerId!);
        if (cachedAccount) {
          return cachedAccount;
        }
      }

      // Fallback to individual API call only if not in cache
      // This should rarely happen as the global cache is loaded above
      return api.partners.getPartnerIdAccount(partnerId!);
    },
    enabled: !!isValidUUID && !!globalAccountsCache, // Only query after global cache is loaded
    staleTime: 5 * 60 * 1000, // 5 minutes - matches global cache stale time
  });
}
